import type { Dispatch, SetStateAction } from "react";

/**
 * Optimistic list mutation: apply the change to local state immediately, run the
 * async commit, and roll back to the pre-change snapshot if the commit throws —
 * then rethrow so the caller's existing error/toast handling still runs.
 *
 * `reconcile` (optional) runs after a successful commit to fold server-assigned
 * data back in (e.g. swap a temp id for the real one returned by create).
 *
 * Usage:
 *   await optimisticListMutate(
 *     setItems,
 *     (prev) => prev.filter((x) => x.id !== id),   // apply
 *     () => deleteApi(id),                          // commit
 *   );
 */
export async function optimisticListMutate<T>(
  setList: Dispatch<SetStateAction<T[]>>,
  apply: (prev: T[]) => T[],
  commit: () => Promise<void>,
  reconcile?: (prev: T[]) => T[],
): Promise<void> {
  let snapshot: T[] = [];
  setList((prev) => {
    snapshot = prev;
    return apply(prev);
  });
  try {
    await commit();
    if (reconcile) setList(reconcile);
  } catch (e) {
    setList(snapshot);
    throw e;
  }
}
